import { useCallback, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import ExpandableCard from '../../../../../../shared/components/Cards/ExpandableCard/ExpandableCard';
import Badge from '../../../../../../shared/components/Globals/Badge/Badge';
import Toast from '../../../../../../shared/components/Globals/Toast/Toast';
import DiscardQuotesModal from '../../../../../../shared/components/Modals/DiscardQuotesModal/DiscardQuotesModal';
import BillingContactCard from '../../../../../../shared/components/ProfileCards/BillingContactCard/BillingContactCard';
import CompanyCard, {
  type ICompanyCardHandle
} from '../../../../../../shared/components/ProfileCards/CompanyCard/CompanyCard';
import PrimaryContactCard from '../../../../../../shared/components/ProfileCards/PrimaryContactCard/PrimaryContactCard';
import type {
  IBillingDraftFields,
  IContactDraftFields,
  ICreateProspectContact,
  IProspect,
  IUpdateProspectContact,
  IUpdateProspectInput
} from '../../../../../../shared/interfaces/prospects';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';
import type { ProspectSalesModel } from '../../../../../../shared/types/prospects';
import { buildProspectActivationChecks } from '../../../../../../shared/utils/checks/activationChecks';
import { getSalesModel } from '../../../../../../shared/utils/prospects/prospects';
import CardEditButtons from '../../components/CardEditButtons/CardEditButtons';

import '../../../../../../shared/components/ProfileCards/ClientDataCardLayout/ClientDataCardLayout.scss';

export interface IClientDataSavedResult {
  salesModelChanged: boolean;
}

interface IClientDataCardProps {
  prospect: IProspect;
  onUpdate: (input: IUpdateProspectInput) => Promise<void>;
  onCreateContact: (input: ICreateProspectContact) => Promise<void>;
  onUpdateContact: (contactId: number, input: IUpdateProspectContact) => Promise<void>;
  onCompanyDraftChange?: (overrides: Partial<IProspect> | null) => void;
  onContactDraftChange?: (draft: IContactDraftFields | null) => void;
  onBillingDraftChange?: (draft: IBillingDraftFields | null) => void;
  onSaved?: (result: IClientDataSavedResult) => Promise<void>;
  savedQuotesCount?: number;
}

const ClientDataCard = ({
  prospect,
  onUpdate,
  onCreateContact,
  onUpdateContact,
  onCompanyDraftChange,
  onContactDraftChange,
  onBillingDraftChange,
  onSaved,
  savedQuotesCount = 0
}: IClientDataCardProps) => {
  const { t } = useTranslation();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [companyValid, setCompanyValid] = useState(true);
  const [primaryValid, setPrimaryValid] = useState(true);
  const [billingValid, setBillingValid] = useState(true);
  const [billingIsSameAsPrimary, setBillingIsSameAsPrimary] = useState(true);
  const [pendingSalesModel, setPendingSalesModel] = useState<ProspectSalesModel | null>(null);
  const [lockedByHeadcount, setLockedByHeadcount] = useState(false);
  const [companyBlocker, setCompanyBlocker] = useState<string | undefined>(undefined);

  const isDataComplete = buildProspectActivationChecks(prospect).every((check) => check.done);
  const canSave = companyValid && primaryValid && billingValid;

  const companyRef = useRef<ICompanyCardHandle>(null);
  const primaryRef = useRef<{ save: () => Promise<void>; getDraft: () => IContactDraftFields }>(null);
  const billingRef = useRef<{ save: () => Promise<void> }>(null);

  const start = useCallback(() => {
    setHasFailed(false);
    setEditing(true);
  }, []);

  const cancel = useCallback(() => {
    setHasFailed(false);
    setPendingSalesModel(null);
    setEditing(false);
  }, []);

  const keepSalesModel = useCallback(() => {
    companyRef.current?.resetSalesModel();
    setPendingSalesModel(null);
  }, []);

  const commit = useCallback(async () => {
    const nextSalesModel = companyRef.current?.getSalesModel();
    const salesModelChanged = !!nextSalesModel && nextSalesModel !== getSalesModel(prospect);
    setPendingSalesModel(null);
    setSaving(true);
    setHasFailed(false);
    try {
      const [companyResult, primaryResult] = await Promise.allSettled([
        companyRef.current?.save() ?? Promise.resolve(),
        primaryRef.current?.save() ?? Promise.resolve()
      ]);

      const shouldSaveBilling = !billingIsSameAsPrimary || primaryResult.status === 'fulfilled';
      const [billingResult] = shouldSaveBilling
        ? await Promise.allSettled([billingRef.current?.save() ?? Promise.resolve()])
        : ([{ status: 'rejected', reason: new Error('Primary contact save failed') }] as const);

      const results = [companyResult, primaryResult, billingResult];
      const hasFailures = results.some((result) => result.status === 'rejected');
      const hasSuccesses = results.some((result) => result.status === 'fulfilled');

      if (hasSuccesses) {
        await onSaved?.({ salesModelChanged });
      }

      if (hasFailures) {
        setHasFailed(true);
        return;
      }

      setEditing(false);
    } catch {
      setHasFailed(true);
    } finally {
      setSaving(false);
    }
  }, [billingIsSameAsPrimary, onSaved, prospect]);

  const requestSave = useCallback(() => {
    const nextSalesModel = companyRef.current?.getSalesModel();
    if (nextSalesModel && nextSalesModel !== getSalesModel(prospect) && savedQuotesCount > 0) {
      setPendingSalesModel(nextSalesModel);
      return;
    }
    void commit();
  }, [prospect, savedQuotesCount, commit]);

  return (
    <ExpandableCard
      className="client-data-card"
      defaultExpanded={false}
      title={t(TRANSLATIONS_PROSPECTS.profile.activation.clientData)}
      trailingComponent={
        <Badge variant={isDataComplete ? 'quoted' : 'inactive'}>
          {isDataComplete
            ? t(TRANSLATIONS_PROSPECTS.profile.activation.complete)
            : t(TRANSLATIONS_PROSPECTS.profile.activation.pendingInfo)}
        </Badge>
      }
    >
      <div className="client-data-card__section">
        <h4 className="client-data-card__section-title">{t(TRANSLATIONS_PROSPECTS.profile.company.title)}</h4>
        <CompanyCard
          key={editing ? 'company-edit' : 'company-view'}
          ref={companyRef}
          prospect={prospect}
          onUpdate={onUpdate}
          onDraftChange={onCompanyDraftChange}
          editing={editing}
          onValidChange={setCompanyValid}
          savedQuotesCount={savedQuotesCount}
          onLockedChange={setLockedByHeadcount}
          onSaveBlockerChange={setCompanyBlocker}
        />
      </div>

      <div className="client-data-card__divider" />

      <div className="client-data-card__section">
        <h4 className="client-data-card__section-title">{t(TRANSLATIONS_PROSPECTS.profile.primaryContact.title)}</h4>
        <PrimaryContactCard
          key={editing ? 'primary-edit' : 'primary-view'}
          ref={primaryRef}
          prospect={prospect}
          onCreateContact={onCreateContact}
          onUpdateContact={onUpdateContact}
          onDraftChange={onContactDraftChange}
          editing={editing}
          onValidChange={setPrimaryValid}
          billingIsSameAsPrimary={billingIsSameAsPrimary}
          disabled={lockedByHeadcount}
        />
      </div>

      <div className="client-data-card__divider" />

      <div className="client-data-card__section">
        <h4 className="client-data-card__section-title">{t(TRANSLATIONS_PROSPECTS.profile.billingContact.title)}</h4>
        <BillingContactCard
          key={editing ? 'billing-edit' : 'billing-view'}
          ref={billingRef}
          prospect={prospect}
          onCreateContact={onCreateContact}
          onUpdateContact={onUpdateContact}
          editing={editing}
          onValidChange={setBillingValid}
          onSameAsPrimaryChange={setBillingIsSameAsPrimary}
          onDraftChange={onBillingDraftChange}
          getPrimaryDraft={() => primaryRef.current?.getDraft() ?? null}
          disabled={lockedByHeadcount}
        />
      </div>

      <div className="client-data-card__footer">
        {editing ? (
          <CardEditButtons
            onCancel={cancel}
            onSave={requestSave}
            loading={saving}
            saveDisabled={!canSave}
            saveTooltip={companyBlocker}
          />
        ) : (
          <CustomButton size="s" variant="default" onClick={start}>
            {t(TRANSLATIONS_PROSPECTS.profile.actions.edit)}
          </CustomButton>
        )}
      </div>

      {pendingSalesModel && (
        <DiscardQuotesModal
          open
          savedQuotesCount={savedQuotesCount}
          currentSalesModel={getSalesModel(prospect)}
          nextSalesModel={pendingSalesModel}
          onKeep={keepSalesModel}
          onConfirm={() => void commit()}
        />
      )}

      <Toast
        open={hasFailed}
        message={t(TRANSLATIONS_PROSPECTS.profile.saveError)}
        variant="danger"
        onClose={() => setHasFailed(false)}
      />
    </ExpandableCard>
  );
};

export default ClientDataCard;
